import { inject, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { translateFeature } from '@contler/translate/state';
import { getActualLan } from '@contler/translate/utils';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Type definition for translation object input
 * Example: { "en-US": "water", "es-CO": "agua", "fr-FR": "eau" }
 */
export type TranslationObject = Record<string, string>;

/**
 * Pipe that takes a translation object and returns the value
 * corresponding to the current language.
 * Automatically reacts to language changes.
 *
 * @example
 * ```html
 * {{ translationObj | translateObject }}
 * ```
 *
 * Where translationObj is:
 * ```typescript
 * {
 *   "en-US": "pure water",
 *   "es-CO": "agua pura",
 *   "fr-FR": "eau pure"
 * }
 * ```
 */
@Pipe({
  name: 'translateObject',
  pure: false,
})
export class TranslateObjectPipe implements PipeTransform, OnDestroy {
  private store = inject(Store);
  private translate = inject(TranslateService);

  private currentLang: string | null = null;
  private subscription: Subscription | null = null;

  constructor() {
    this.subscription = this.store.select(translateFeature.selectActualLanguage).subscribe((language) => {
      this.currentLang = language?.prefix ?? this.translate.currentLang ?? getActualLan();
    });
  }

  /**
   * Transforms a translation object into the corresponding value for the current language
   * @param value - Object containing translations keyed by language code
   * @returns The translation for the current language
   */
  transform(value: TranslationObject | undefined | null): string {
    if (!value || typeof value !== 'object') {
      return '';
    }

    const lang = this.currentLang ?? this.translate.currentLang ?? getActualLan();

    if (value[lang]) {
      return value[lang];
    }

    if (lang.includes('pt')) {
      return value['pt-PT'] || value['pt-BR'] || this.getFallbackValue(value);
    }

    const langPrefix = lang.split('-')[0];
    const matchingKey = Object.keys(value).find((key) => key.startsWith(langPrefix));
    if (matchingKey) {
      return value[matchingKey];
    }

    return this.getFallbackValue(value);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /**
   * Returns the first available translation or empty string
   * @param value - Translation object
   * @returns First available translation value or empty string
   */
  private getFallbackValue(value: TranslationObject): string {
    const keys = Object.keys(value);
    return keys.length > 0 ? value[keys[0]] : '';
  }
}
